import api from './api';

export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  // Role for access control: 'admin' or 'user' (default)
  role: 'admin' | 'user';
  // Job position (e.g., 'Sales Agent', 'Sales Manager', etc.)
  position?: string;
  // Legacy: primaryRole kept for backward compatibility
  primaryRole:
    | 'admin'
    | 'sales_manager'
    | 'sales_agent'
    | 'rental_agent'
    | 'sales_assistant'
    | 'rental_assistant'
    | 'property_manager'
    | 'photographer'
    | 'styler';
  roles: string[]; // Array of all active roles (legacy)
  phone: string;
  avatar?: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserRoleRequest {
  role?: 'admin' | 'user';
  position?: string;
}

export interface UpdateUserActiveStatusRequest {
  isActive: boolean;
}

export interface AddUserRoleRequest {
  role:
    | 'admin'
    | 'sales_manager'
    | 'sales_agent'
    | 'rental_agent'
    | 'sales_assistant'
    | 'rental_assistant'
    | 'property_manager'
    | 'photographer'
    | 'styler';
}

export interface UpdatePrimaryRoleRequest {
  primaryRole:
    | 'admin'
    | 'sales_manager'
    | 'sales_agent'
    | 'rental_agent'
    | 'sales_assistant'
    | 'rental_assistant'
    | 'property_manager'
    | 'photographer'
    | 'styler';
}

export interface UserRole {
  _id: string;
  userId: string;
  agencyId: string;
  role: string;
  isActive: boolean;
  assignedBy: string;
  assignedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserRoleResponse {
  success: boolean;
  message: string;
  user: User;
}

export interface UpdateUserActiveStatusResponse {
  message: string;
  user: User;
}

class AgencyService {
  async getTeamMembers(agencySlug: string): Promise<User[]> {
    const response = await api.get(`/agencies/${agencySlug}/users`);
    return response.data;
  }

  async updateUserRole(
    agencySlug: string, 
    userId: string, 
    data: UpdateUserRoleRequest
  ): Promise<UpdateUserRoleResponse> {
    const response = await api.patch(`/agencies/${agencySlug}/users/${userId}/role`, data);
    return response.data;
  }

  async updateUserActiveStatus(
    agencySlug: string, 
    userId: string, 
    data: UpdateUserActiveStatusRequest
  ): Promise<UpdateUserActiveStatusResponse> {
    const response = await api.patch(`/agencies/${agencySlug}/users/${userId}/active`, data);
    return response.data;
  }

  // Multi-role management
  async getUserRoles(agencySlug: string, userId: string) {
    const response = await api.get(`/agencies/${agencySlug}/users/${userId}/roles`);
    return response.data;
  }

  async addUserRole(agencySlug: string, userId: string, data: AddUserRoleRequest) {
    const response = await api.post(`/agencies/${agencySlug}/users/${userId}/roles`, data);
    return response.data;
  }

  async removeUserRole(agencySlug: string, userId: string, role: string) {
    const response = await api.delete(`/agencies/${agencySlug}/users/${userId}/roles/${role}`);
    return response.data;
  }

  async updatePrimaryRole(agencySlug: string, userId: string, data: UpdatePrimaryRoleRequest) {
    const response = await api.patch(`/agencies/${agencySlug}/users/${userId}/primary-role`, data);
    return response.data;
  }

  async deleteTeamMember(agencySlug: string, userId: string) {
    const response = await api.delete(`/agencies/${agencySlug}/users/${userId}`);
    return response.data;
  }
}

export const agencyService = new AgencyService();
